import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Group, SiteMini, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { saveBlob } from '../../components/ExportButton';
import { SelectField } from '../../components/ui';

/** Shared bits of the site-features pages (W20): lists to pick from, and opening a downloaded PDF. */

const SitePicker = page(SiteMini.extend({ isRemote: z.boolean().optional() }));
/** Not the full `Site`: GET /sites answers with SiteMini objects (id and name only) to people without sites.view. */
const SiteFull = page(SiteMini.extend({ timeZone: z.string().optional(), isRemote: z.boolean().optional() }));
const GroupList = z.array(Group);

/** Active sites this person can see. Remote sites are left out unless asked for. */
export function useSiteChoices(opts: { remote?: boolean } = {}) {
  return useQuery({
    queryKey: ['site-features', 'sites'],
    queryFn: () => api.get('/sites', { query: { status: 'active', pageSize: 200 }, schema: SitePicker }),
    select: (r) => ({ ...r, items: r.items.filter((s) => (opts.remote ? true : s.isRemote !== true)) }),
  });
}

export function useFullSites() {
  return useQuery({
    queryKey: ['site-features', 'sites-full'],
    queryFn: () => api.get('/sites', { query: { status: 'active', pageSize: 200 }, schema: SiteFull }),
  });
}

export function useActiveGroups() {
  return useQuery({ queryKey: ['site-features', 'groups'], queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }) });
}

/** A site drop-down that picks the first site when nothing is chosen yet. `allowAll` adds an "All sites" entry (empty value). */
export function SiteSelect({
  value,
  onChange,
  allowAll = false,
  label,
}: {
  value: string;
  onChange: (id: string) => void;
  allowAll?: boolean;
  label?: string;
}) {
  const sites = useSiteChoices();
  const items = sites.data?.items ?? [];
  const first = items[0]?.id ?? '';
  useEffect(() => {
    if (!allowAll && value === '' && first !== '') onChange(first);
  }, [allowAll, value, first, onChange]);
  return (
    <SelectField label={label ?? t('siteF.site')} value={value} onChange={(e) => onChange(e.target.value)}>
      {allowAll ? <option value="">{t('siteF.allSites')}</option> : null}
      {items.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </SelectField>
  );
}

/** Opens a downloaded PDF in a new tab; falls back to saving it where pop-ups are blocked. */
export function openPdf(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) saveBlob(blob, fileName);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const ALL_MODES = ['in', 'out', 'both'] as const;
